/**
 * Analytics event catalogue for login-web.
 *
 * Naming: `login.<flow>.<action>` (lowercase, dotted), the same taxonomy as
 * mission-control-web's `mc.*` events. Every event has a typed payload in
 * `EventSchema`; `AnalyticsService.capture` enforces it at compile time.
 *
 * PII: never put an email, name, code or token in a payload. Customer ids are
 * fine (they are not personal data). The visitor stays anonymous here — the
 * app they land in identifies them, and PostHog merges these events into that
 * person through the shared *.ricohworkplace.com cookie. That cookie exists
 * only when the visitor accepted analytics in the consent banner; otherwise
 * events are cookieless and stay unlinked.
 */
import type { RedirectFailureReason } from '../login.service';

export const ANALYTICS_EVENTS = {
  EMAIL_SUBMITTED: 'login.email.submitted',
  ACCOUNT_LOOKUP_COMPLETED: 'login.email.lookup_completed',
  SIGN_IN_STARTED: 'login.sign_in.started',
  SIGN_IN_SUCCEEDED: 'login.sign_in.succeeded',
  SIGN_IN_CANCELLED: 'login.sign_in.cancelled',
  SIGN_IN_FAILED: 'login.sign_in.failed',
  AUTH_ERROR_DISMISSED: 'login.auth_error.dismissed',
  MAGIC_LINK_DIALOG_OPENED: 'login.magic_link.dialog_opened',
  MAGIC_LINK_REQUESTED: 'login.magic_link.requested',
  EXISTING_SESSION_SHOWN: 'login.existing_session.shown',
  EXISTING_SESSION_CONTINUED: 'login.existing_session.continued',
  EXISTING_SESSION_SWITCH_ACCOUNT: 'login.existing_session.switch_account_clicked',
  LOGOUT_COMPLETED: 'login.logout.completed',
  LOGOUT_CONTINUE_TO_LOGIN: 'login.logout.continue_to_login_clicked',
  ERROR_OCCURRED: 'login.session.error_occurred',
  CONSENT_CHANGED: 'login.consent.changed',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Sign-in methods, named as `hasLoginMethod()` names them. */
export type SignInMethod = 'microsoft' | 'google' | 'okta' | 'magic-link';

/**
 * How the attempt was driven: a provider popup, a full-page provider redirect
 * (embedded browsers), a magic-link deep link, or a code typed into the dialog.
 */
export type SignInFlow = 'popup' | 'redirect' | 'deep-link' | 'code-entry';

/** Where a failed attempt broke. */
export type SignInFailureStage = 'sign_in' | 'config' | 'redirect';

interface SignInBase {
  method: SignInMethod;
  flow: SignInFlow;
}

interface SignInOutcome extends SignInBase {
  /** Time from the start of the attempt; null when the start wasn't observed (redirect returns). */
  duration_ms: number | null;
}

export interface EventSchema {
  [ANALYTICS_EVENTS.EMAIL_SUBMITTED]: {
    /** The visit already had a customer context (?customer= or a division host). */
    has_customer_context: boolean;
    division_context_locked: boolean;
  };
  [ANALYTICS_EVENTS.ACCOUNT_LOOKUP_COMPLETED]: {
    outcome: 'found' | 'not_found' | 'error';
    customer_id: string | null;
    login_methods: string[];
    login_method_count: number;
    latency_ms: number;
  };
  [ANALYTICS_EVENTS.SIGN_IN_STARTED]: SignInBase;
  [ANALYTICS_EVENTS.SIGN_IN_SUCCEEDED]: SignInOutcome;
  [ANALYTICS_EVENTS.SIGN_IN_CANCELLED]: SignInOutcome;
  [ANALYTICS_EVENTS.SIGN_IN_FAILED]: SignInOutcome & {
    stage: SignInFailureStage;
    redirect_failure_reason: RedirectFailureReason | null;
  };
  [ANALYTICS_EVENTS.AUTH_ERROR_DISMISSED]: {
    tone: 'error' | 'notice';
  };
  [ANALYTICS_EVENTS.MAGIC_LINK_DIALOG_OPENED]: {
    source: 'button' | 'deep-link-fallback';
  };
  [ANALYTICS_EVENTS.MAGIC_LINK_REQUESTED]: {
    outcome: 'sent' | 'failed';
    is_resend: boolean;
  };
  [ANALYTICS_EVENTS.EXISTING_SESSION_SHOWN]: {
    /** Seconds until the stored token expires. */
    token_ttl_s: number;
  };
  [ANALYTICS_EVENTS.EXISTING_SESSION_CONTINUED]: {
    outcome: 'redirected' | 'redirect_failed';
    redirect_failure_reason: RedirectFailureReason | null;
  };
  [ANALYTICS_EVENTS.EXISTING_SESSION_SWITCH_ACCOUNT]: {
    source: 'continue-panel';
  };
  [ANALYTICS_EVENTS.LOGOUT_COMPLETED]: {
    outcome: 'signed_out' | 'failed';
    attempt: number;
  };
  [ANALYTICS_EVENTS.LOGOUT_CONTINUE_TO_LOGIN]: {
    after_failure: boolean;
  };
  [ANALYTICS_EVENTS.ERROR_OCCURRED]: {
    error_type: string;
    error_message: string;
  };
  [ANALYTICS_EVENTS.CONSENT_CHANGED]: {
    analytics: boolean;
    recordings: boolean;
    /** ThoughtSpot's report usage analytics (Mixpanel). */
    reports: boolean;
    /** Consent revision the choice was made under. */
    revision: number;
    /** First choice on this browser, as opposed to a later change in preferences. */
    first_choice: boolean;
    /** The consent library's id for this choice; not personal data on its own. */
    consent_id: string | null;
  };
}
